import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import AssignmentIcon from '@mui/icons-material/AssignmentOutlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import PendingActionsIcon from '@mui/icons-material/PendingActionsOutlined';
import LocalShippingIcon from '@mui/icons-material/LocalShippingOutlined';
import { apiClient } from '@/api/axiosClient';
import StatCard from '@/components/common/StatCard';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import DashboardLineChart from '@/components/dashboard/DashboardLineChart';
import DashboardPieChart from '@/components/dashboard/DashboardPieChart';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { groupCountBy, groupSumByMonth, topN } from '@/lib/dashboardAggregations';
import { withId } from '@/pages/reports/reportHelpers';
import type { PurchaseOrderRegisterRow, PurchaseRequestRegisterRow, SupplierPerformanceRow } from '@/types/reports';

const formatCurrency = (v: number) => `₹${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const STATUS_SLICE_COLORS = ['#0566d9', '#ffb951', '#4edea3', '#ba1a1a', '#6b6f76', '#3e5c8a'];

export default function ProcurementPanel() {
  const { t } = useTranslation('dashboard');
  const { t: tr } = useTranslation('reports');
  const colors = useCategoryColors();

  const { data: purchaseOrders = [], isLoading: loadingPo } = useQuery({
    queryKey: ['reports', 'purchase-order-register'],
    queryFn: async () => (await apiClient.get<PurchaseOrderRegisterRow[]>('/reports/purchase-order-register')).data,
  });
  const { data: purchaseRequests = [] } = useQuery({
    queryKey: ['reports', 'purchase-request-register'],
    queryFn: async () => (await apiClient.get<PurchaseRequestRegisterRow[]>('/reports/purchase-request-register')).data,
  });
  const { data: supplierPerformance = [], isLoading: loadingSupplier } = useQuery({
    queryKey: ['reports', 'supplier-performance'],
    queryFn: async () => (await apiClient.get<SupplierPerformanceRow[]>('/reports/supplier-performance')).data,
  });

  const totalPoCount = useMemo(() => new Set(purchaseOrders.map((r) => r.poId)).size, [purchaseOrders]);
  const totalPoValue = useMemo(() => purchaseOrders.reduce((sum, r) => sum + r.orderedQty * r.unitPrice, 0), [purchaseOrders]);
  const pendingPrCount = useMemo(() => new Set(purchaseRequests.filter((r) => !r.isClosed).map((r) => r.prId)).size, [purchaseRequests]);
  const avgOnTime = useMemo(() => {
    const values = supplierPerformance.map((s) => s.onTimeDeliveryPercent).filter((v): v is number => v != null);
    if (values.length === 0) return null;
    return values.reduce((sum, v) => sum + v, 0) / values.length;
  }, [supplierPerformance]);

  const monthlyTrend = useMemo(
    () => groupSumByMonth(purchaseOrders, (r) => r.orderDate, [{ key: 'value', value: (r) => r.orderedQty * r.unitPrice }]),
    [purchaseOrders],
  );
  const topSuppliers = useMemo(() => topN(supplierPerformance, (r) => r.totalOrderedQty, 5).reverse(), [supplierPerformance]);
  const statusDistribution = useMemo(() => groupCountBy(purchaseOrders, (r) => r.status), [purchaseOrders]);

  const supplierColumns: DataTableColumn<ReturnType<typeof withId<SupplierPerformanceRow>>[number]>[] = [
    { field: 'supplierName', headerName: tr('columns.supplier') },
    { field: 'totalPurchaseOrders', headerName: t('procurement.totalPos'), align: 'right' },
    { field: 'totalOrderedQty', headerName: tr('columns.orderedQty'), align: 'right' },
    { field: 'onTimeDeliveryPercent', headerName: t('procurement.onTimePercent'), align: 'right', render: (r) => (r.onTimeDeliveryPercent != null ? `${r.onTimeDeliveryPercent.toFixed(0)}%` : '—') },
    { field: 'rejectionRatePercent', headerName: t('procurement.rejectionPercent'), align: 'right', render: (r) => (r.rejectionRatePercent != null ? `${r.rejectionRatePercent.toFixed(1)}%` : '—') },
  ];

  return (
    <Stack spacing={3}>
      <GridContainer columns={12}>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('procurement.totalPos')} value={totalPoCount} icon={<AssignmentIcon />} accentColor={colors.procurement} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('procurement.totalPoValue')} value={formatCurrency(totalPoValue)} icon={<PaymentsIcon />} accentColor={colors.procurement} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('procurement.pendingPrs')} value={pendingPrCount} icon={<PendingActionsIcon />} accentColor={colors.procurement} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('procurement.avgOnTime')} value={avgOnTime != null ? `${avgOnTime.toFixed(0)}%` : '—'} icon={<LocalShippingIcon />} accentColor={colors.procurement} />
        </GridItem>
      </GridContainer>

      <GridContainer columns={12} spacing={2}>
        <GridItem xs={12} md={7}>
          <ChartCard title={t('procurement.trendTitle')} subtitle={t('procurement.trendSubtitle')} loading={loadingPo} empty={!loadingPo && monthlyTrend.length === 0}>
            <DashboardLineChart
              categories={monthlyTrend.map((m) => m.month)}
              series={[{ label: tr('columns.value'), data: monthlyTrend.map((m) => m.values.value), color: colors.procurement, area: true }]}
              valueFormatter={(v) => formatCurrency(v ?? 0)}
            />
          </ChartCard>
        </GridItem>
        <GridItem xs={12} md={5}>
          <ChartCard title={t('procurement.statusTitle')} loading={loadingPo} empty={!loadingPo && statusDistribution.length === 0}>
            <DashboardPieChart data={statusDistribution.map((s, i) => ({ label: s.key, value: s.count, color: STATUS_SLICE_COLORS[i % STATUS_SLICE_COLORS.length] }))} />
          </ChartCard>
        </GridItem>
      </GridContainer>

      <ChartCard title={t('procurement.topSuppliersTitle')} loading={loadingSupplier} empty={!loadingSupplier && topSuppliers.length === 0} height={280}>
        <DashboardBarChart
          layout="horizontal"
          categories={topSuppliers.map((s) => s.supplierName)}
          series={[{ label: tr('columns.orderedQty'), data: topSuppliers.map((s) => s.totalOrderedQty), color: colors.procurement }]}
          height={280}
        />
      </ChartCard>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          {t('procurement.supplierTableTitle')}
        </Typography>
        <DataTable columns={supplierColumns} rows={withId(supplierPerformance, (r) => r.supplierId)} loading={loadingSupplier} exportable={false} defaultRowsPerPage={5} rowsPerPageOptions={[5, 10, 25]} />
      </Paper>
    </Stack>
  );
}
